import React, { useEffect, useState } from "react";
import { Info, X, Bed, Mic, Activity, Book, Cpu, Zap } from "lucide-react";
import { useInfoPoint } from "../context/InfoPointsContext";

const CATEGORY_ICONS = {
  sleep: Bed,
  physio: Activity,
  audio: Mic,
  diary: Book,
  sensor: Cpu,
  algo: Zap,
};

/**
 * InfoPoint — piccola icona ⓘ che on-click apre un popover con:
 *   titolo | breve | come viene calcolato | sorgente
 * I testi arrivano da MongoDB (collection `infopoints`), editabili dall'admin.
 *
 * Uso: <InfoPoint keyName="score_mattress" />
 */
export default function InfoPoint({ keyName, className = "", size = 13 }) {
  const [open, setOpen] = useState(false);
  const { data } = useInfoPoint(keyName);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!data) {
    // Nessun infopoint trovato → non rompere la UI
    return null;
  }

  const Icon = CATEGORY_ICONS[data.category] || Info;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={`inline-flex items-center justify-center rounded-full w-[18px] h-[18px] bg-white/8 hover:bg-[#14c2a1]/20 text-white/45 hover:text-[#67e8d1] transition-colors align-middle ${className}`}
        data-testid={`infopoint-${keyName}`}
        aria-label={`Info su ${data.title}`}
      >
        <Info size={size - 2} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/65 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`infopoint-modal-${keyName}`}
        >
          <div
            className="w-full sm:w-[min(420px,92vw)] bg-[#0e1517] border border-white/10 sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-4 flex items-start gap-3 border-b border-white/5">
              <div className="shrink-0 w-9 h-9 rounded-xl bg-[#14c2a1]/15 text-[#67e8d1] flex items-center justify-center">
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-[15px] font-semibold leading-tight">
                  {data.title}
                </div>
                <div className="text-white/55 text-[11.5px] mt-0.5 uppercase tracking-wider">
                  {catLabel(data.category)}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 flex items-center justify-center shrink-0"
              >
                <X size={14} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3.5">
              {data.short && (
                <p className="text-white/90 text-[13.5px] leading-snug">
                  {data.short}
                </p>
              )}
              {data.how && (
                <div>
                  <div className="text-[#67e8d1] text-[10.5px] uppercase tracking-wider mb-1 font-semibold">
                    Come viene calcolato
                  </div>
                  <p className="text-white/75 text-[12.5px] leading-relaxed">
                    {data.how}
                  </p>
                </div>
              )}
              {data.source && (
                <div className="pt-2 border-t border-white/5 flex items-center gap-2 text-[11px] text-white/50">
                  <span className="uppercase tracking-wider">Sorgente</span>
                  <span className="text-white/75">{data.source}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function catLabel(cat) {
  const labels = {
    sleep: "Sonno",
    physio: "Fisiologia",
    audio: "Audio",
    diary: "Diario",
    sensor: "Sensore",
    algo: "Algoritmo",
  };
  return labels[cat] || cat;
}
